import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Camera, Loader2, Plus, Trash2, X, Users, Star, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AnonymousQuestions from "@/components/AnonymousQuestions";
import { logError } from "@/lib/log";
import { useLanguage } from "@/context/LanguageContext";

async function prepareProfileImage(file) {
  // Accept large phone photos, but optimize them in-browser before sending.
  // This keeps uploads fast and avoids proxy request-size limits.
  const TARGET_BYTES = 900 * 1024;
  const MAX_DIMENSION = 1200;
  if (file.size <= TARGET_BYTES && ["image/jpeg", "image/webp"].includes(file.type)) return file;
  if (file.type === "image/gif" && file.size <= TARGET_BYTES) return file;

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not prepare the profile photo.");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  let quality = 0.82;
  let blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  while (blob && blob.size > TARGET_BYTES && quality > 0.45) {
    quality -= 0.07;
    blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  }
  if (!blob) throw new Error("Could not prepare the profile photo.");
  return new File([blob], "profile.jpg", { type: "image/jpeg", lastModified: Date.now() });
}

export default function ProfilePage() {
  const { t } = useLanguage();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [followLists, setFollowLists] = useState({ followers: [], following: [] });
  const [followView, setFollowView] = useState(null);
  const [followLoading, setFollowLoading] = useState(false);
  const [bio, setBio] = useState(user?.bio || "");
  const [status, setStatus] = useState(user?.status_message || "");
  const [interests, setInterests] = useState(user?.interests || []);
  const [interestInput, setInterestInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bannerUrl, setBannerUrl] = useState(user?.profile_banner_url || null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [progression, setProgression] = useState(null);
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => {
      setBannerUrl(data.profile_banner_url || null);
      setUser(data);
      setBio(data.bio || "");
      setStatus(data.status_message || "");
      setInterests(data.interests || []);
    }).catch((e) => logError("ProfilePageMe", e));
  }, [setUser]);

  useEffect(() => {
    (async () => {
      try {
        const [photosResponse, progressionResponse] = await Promise.all([
          api.get("/profile/photos"),
          api.get("/progression/me"),
        ]);
        setPhotos(photosResponse.data || []);
        setProgression(progressionResponse.data || null);
      } catch (e) {
        logError("ProfilePage", e);
        try {
          const { data } = await api.get("/profile/photos");
          setPhotos(data || []);
        } catch (photoError) {
          logError("ProfilePagePhotos", photoError);
        }
        try {
          const { data } = await api.get("/progression/me");
          setProgression(data || null);
        } catch (progressionError) {
          logError("ProfilePageProgression", progressionError);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!user?.username) return;
    let active = true;
    const username = encodeURIComponent(user.username);
    Promise.all([
      api.get(`/users/${username}/followers`),
      api.get(`/users/${username}/following`),
    ]).then(([followers, following]) => {
      if (active) setFollowLists({ followers: followers.data || [], following: following.data || [] });
    }).catch(() => { if (active) toast.error(t("Could not load follow counts.")); });
    return () => { active = false; };
  }, [user?.username, t]);

  const openFollowList = async (kind) => {
    setFollowView(kind);
    if (!user?.username) return;
    setFollowLoading(true);
    try {
      const { data } = await api.get(`/users/${encodeURIComponent(user.username)}/${kind}`);
      setFollowLists((current) => ({ ...current, [kind]: data || [] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFollowLoading(false); }
  };

  const addInterest = () => {
    const v = interestInput.trim();
    if (v && !interests.includes(v) && interests.length < 12) {
      setInterests([...interests, v]);
    }
    setInterestInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile/me", {
        bio,
        status_message: status,
        interests,
      });
      setUser(data);
      toast.success(t("Profile updated"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("Please choose an image."));
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t("Profile photo must be 100MB or smaller."));
      return;
    }
    setAvatarUploading(true);
    try {
      const prepared = await prepareProfileImage(file);
      const form = new FormData();
      form.append("file", prepared);
      const { data } = await api.post("/profile/avatar", form);
      setUser(data);
      toast.success(t("Profile photo updated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAvatarUploading(false);
    }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error(t("Banner must be 100MB or smaller.")); return; }
    setBannerUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/profile/banner", form);
      setBannerUrl(data.profile_banner_url || null);
      setUser(data);
      toast.success(t("Profile banner updated."));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBannerUploading(false);
    }
  };

  const removeBanner = async () => {
    setBannerUploading(true);
    try {
      const { data } = await api.delete("/profile/banner");
      setBannerUrl(null);
      setUser(data);
      toast.success(t("Profile banner removed."));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBannerUploading(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/profile/photos", form);
      setPhotos((p) => [{ id: data.id, url: data.url, created_at: new Date().toISOString() }, ...p]);
      toast.success(t("Photo added to gallery"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setPhotoUploading(false);
  };

  const deletePhoto = async (id) => {
    try {
      await api.delete(`/profile/photos/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast("Photo removed");
    } catch (e) {
      toast.error(t("Could not remove photo"));
    }
  };

  const initial = (user?.username || "?").charAt(0).toUpperCase();

  return (
    <AppShell>
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          {t("Your Profile")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("This is how the world sees you on Yaprio.")}</p>
      </div>
      {progression && (
        <Card className="rounded-3xl border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-5 shadow-sm" data-testid="profile-progression-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 fill-orange-400 text-orange-400" />
                <h2 className="font-heading text-lg font-extrabold text-slate-900">{t("Level")} {progression.level}</h2>
              </div>
              <p className="mt-1 text-xs font-semibold text-slate-500">{progression.xp} {t("XP total")}</p>
            </div>
            <div className="text-right">
              <Trophy className="ml-auto h-5 w-5 text-orange-500" />
              <p className="mt-1 text-xs font-bold text-orange-600">
                {progression.achievements?.filter((a) => a.unlocked).length || 0} {t("achievements")}
              </p>
            </div>
          </div>
          <div className="mt-4">
             <div className="mb-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
               <span>{progression.level >= 100 ? t("MAX LEVEL") : progression.current_level_xp + " / " + progression.next_level_xp + " XP"}</span>
               <span>{progression.level >= 100 ? "100%" : Math.round(Math.min(100, (progression.current_level_xp / Math.max(1, progression.next_level_xp)) * 100)) + "%"}</span>
             </div>
             <div className="h-3 w-full overflow-hidden rounded-full border border-orange-100 bg-orange-50 shadow-inner">
               <div
                 className="h-full rounded-full"
                 style={{ width: (progression.level >= 100 ? 100 : Math.min(100, (progression.current_level_xp / Math.max(1, progression.next_level_xp)) * 100)) + "%", background: "linear-gradient(90deg, #ff7a18 0%, #ffad4d 100%)" }}
               />
             </div>
           </div>
          {progression.achievements?.filter((a) => a.unlocked).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {progression.achievements.filter((a) => a.unlocked).map((a) => (
                <Badge key={a.id} className="rounded-full bg-white text-orange-600 shadow-sm">
                  <Trophy className="mr-1 h-3 w-3" />{t(a.name)}
                </Badge>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden rounded-3xl border-slate-100 p-0 shadow-sm" data-testid="profile-edit-card">
        <div className="relative">
          <div className="h-36 w-full overflow-hidden bg-gradient-to-r from-orange-100 via-amber-50 to-orange-100 sm:h-44">
            {bannerUrl ? <img src={mediaUrl(bannerUrl)} alt={t("Your profile banner")} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-sm font-medium text-slate-400">{t("Your profile banner preview")}</div>}
          </div>
          <div className="absolute bottom-3 right-3 flex flex-wrap items-center gap-2">
            <input ref={bannerRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={uploadBanner} />
            {["vip", "elite", "legend"].includes(String(user?.yaprio_role || "").toLowerCase()) && <Button type="button" variant="outline" disabled={bannerUploading} onClick={() => bannerRef.current?.click()} className="rounded-full bg-white/95 text-slate-800 shadow-sm">{bannerUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}{bannerUrl ? t("Replace banner") : t("Upload banner")}</Button>}
            {bannerUrl && <Button type="button" variant="outline" disabled={bannerUploading} onClick={removeBanner} className="rounded-full bg-white/95 text-red-600 shadow-sm"><Trash2 className="mr-2 h-4 w-4" />{t("Remove")}</Button>}
          </div>
        </div>
        <div className="p-6 sm:p-8">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <div className="relative">
            <Avatar className="h-24 w-24 ring-4 ring-pink-100">
              <AvatarImage src={mediaUrl(user?.avatar_url)} alt={user?.username} />
              <AvatarFallback className="bg-pink-500 text-2xl font-bold text-white">{initial}</AvatarFallback>
            </Avatar>
            <button
              onClick={() => avatarRef.current?.click()}
              data-testid="btn-upload-avatar"
              className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-white shadow-lg ring-4 ring-white hover:bg-pink-600"
            >
              {avatarUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input ref={avatarRef} type="file" accept="image/*" hidden onChange={uploadAvatar} />
          </div>
          <div className="text-center sm:pt-2 sm:text-left">
            <div className="flex flex-wrap items-center justify-center gap-2 text-center sm:justify-start sm:text-left"><p className="font-heading text-xl font-bold text-slate-900">@{user?.username}</p>{user?.yaprio_role && <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${user.yaprio_role === "vip" ? "bg-amber-50 text-amber-600" : user.yaprio_role === "elite" ? "bg-cyan-50 text-cyan-600" : "bg-orange-50 text-orange-600"}`}>{user.yaprio_role === "vip" ? "👑 VIP" : user.yaprio_role === "elite" ? "💎 Elite" : "🔥 Legend"}</span>}</div>
            <p className="text-sm text-slate-400">{user?.email}</p>
            <div className="mt-3 flex items-center justify-center gap-5 sm:justify-start">
              {["followers", "following"].map((kind) => <button key={kind} type="button" onClick={() => openFollowList(kind)} className="rounded-xl px-2 py-1 text-sm text-slate-600 transition hover:bg-pink-50 hover:text-pink-600" data-testid={`profile-${kind}`}><span className="font-bold text-slate-900">{followLists[kind].length}</span> <span>{t(kind === "followers" ? "Followers" : "Following")}</span></button>)}
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Status message</Label>
            <Input
              value={status}
              maxLength={100}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="What's your vibe today?"
              data-testid="input-status-message"
              className="h-11 rounded-xl"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">{t("Bio")}</Label>
            <Textarea
              value={bio}
              maxLength={2000}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t("Tell people a little about you…")}
              data-testid="input-bio"
              className="min-h-[140px] rounded-xl"
            />
            <p
              className={`text-right text-xs ${bio.length >= 1900 ? "font-semibold text-pink-500" : "text-slate-400"}`}
              data-testid="bio-char-count"
            >
              {bio.length}/2000
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">{t("Interests")}</Label>
            <div className="flex gap-2">
              <Input
                value={interestInput}
                onChange={(e) => setInterestInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addInterest();
                  }
                }}
                placeholder={t("Add an interest and press Enter")}
                data-testid="input-interest"
                className="h-11 rounded-xl"
              />
              <Button
                type="button"
                onClick={addInterest}
                variant="outline"
                className="h-11 rounded-xl px-4"
                data-testid="btn-add-interest"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {interests.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {interests.map((t) => (
                  <Badge
                    key={t}
                    className="gap-1 rounded-full border-pink-200 bg-pink-50 px-3 py-1 text-pink-600 hover:bg-pink-100"
                    variant="outline"
                  >
                    {t}
                    <button onClick={() => setInterests(interests.filter((x) => x !== t))}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={save}
            disabled={saving}
            data-testid="btn-save-profile"
            className="h-11 rounded-full bg-pink-500 px-8 font-semibold text-white shadow-lg shadow-pink-500/30 hover:bg-pink-600"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Save changes")}
          </Button>
        </div>
        </div>
      </Card>

      {/* Gallery */}
      <Card className="rounded-3xl border-slate-100 p-6 shadow-sm sm:p-8" data-testid="gallery-card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">{t("Photo Gallery")}</h2>
            <p className="text-sm text-slate-500">Show off up to a few of your favourite moments.</p>
          </div>
          <Button
            onClick={() => photoRef.current?.click()}
            data-testid="btn-upload-photo"
            variant="outline"
            className="gap-2 rounded-full font-semibold"
          >
            {photoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t("Add photo")}
          </Button>
          <input ref={photoRef} type="file" accept="image/*" hidden onChange={uploadPhoto} />
        </div>
        {photos.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">{t("No photos yet — add your first one!")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {photos.map((p) => (
              <div key={p.id} className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-100">
                <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
                <button
                  onClick={() => deletePhoto(p.id)}
                  data-testid={`btn-delete-photo-${p.id}`}
                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AnonymousQuestions owner />

      {followView && <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-label={followView === "followers" ? "Your followers" : "People you follow"} onClick={() => setFollowView(null)}>
        <div className="flex max-h-[min(80vh,650px)] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-heading text-lg font-bold capitalize text-slate-900">{followView} <span className="text-sm font-medium text-slate-400">({followLists[followView].length})</span></h2><button type="button" onClick={() => setFollowView(null)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Close list"><X className="h-5 w-5" /></button></div>
          <div className="min-h-0 overflow-y-auto p-3">{followLoading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-pink-500" /></div> : followLists[followView].length === 0 ? <div className="py-12 text-center text-sm text-slate-400"><Users className="mx-auto mb-3 h-8 w-8 text-pink-300" />{followView === "followers" ? "No followers yet." : "You're not following anyone yet."}</div> : followLists[followView].map((person) => <button key={person.user_id} type="button" onClick={() => { setFollowView(null); navigate(`/u/${encodeURIComponent(person.username)}`); }} className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left hover:bg-pink-50"><Avatar className="h-11 w-11"><AvatarImage src={mediaUrl(person.avatar_url)} alt={person.username} /><AvatarFallback className="bg-pink-500 font-bold text-white">{(person.username || "?").charAt(0).toUpperCase()}</AvatarFallback></Avatar><span className="min-w-0 truncate text-sm font-semibold text-slate-900">@{person.username}</span></button>)}</div>
        </div>
      </div>}
    </AppShell>
  );
}